/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {ClaimStatusSchemaBaseModel} from '../../api/ms-claims/contracts/claim-status-schema-base.model';
import {TemplateRef} from '@angular/core';
import {ClaimProgressStepModel} from '../shared/claim-progress/claim-progress-step.model';
import {ClaimStatusBaseModel} from '../../api/ms-claims/contracts/claim-status-base.model';

/**
 * Maps the Claim Status Schema to a Claim Progress Step.
 * @param schemaArray the status schema.
 * @param tooltip the tooltip template reference to be passed to the progress step model.
 * @param rootNode the root element of the progress step model.
 * @param statusNode the actual element of the progress model.
 * @return the Claim Progress Step with the Claim Status Schema information.
 */
export function mapProgressFromStatusSchema(
        schemaArray: ClaimStatusSchemaBaseModel[],
        tooltip: TemplateRef<any>,
        rootNode: ClaimProgressStepModel,
        statusNode: ClaimProgressStepModel = rootNode): ClaimProgressStepModel {
    if (schemaArray != null && schemaArray.length > 0 && rootNode != null) {
        const schema = schemaArray.find(element => element.claimStatus.code === statusNode.code);

        if (schema != null) {
            statusNode.longDescription = schema.claimStatus.longDescription;
            statusNode.isCompleted = true;

            if (schema.justification != null) {
                rootNode.justification = schema.justification;
                statusNode.tooltip = tooltip;
            }
        }

        if (statusNode.nextSteps != null) {
            statusNode.nextSteps.forEach(nextNode => {
                mapProgressFromStatusSchema(schemaArray, tooltip, rootNode, nextNode);
            });
        }
    }

    return statusNode;
}

export function createStatusSchema(
        statusGroups: Map<string, ClaimProgressStepModel[]>,
        current: ClaimProgressStepModel = null,
        groupIndex: number = 0
): ClaimProgressStepModel {
    if (statusGroups == null) {
        return null;
    }

    const currentKey: string = Array.from(statusGroups.keys())[groupIndex];
    const nextKey: string = Array.from(statusGroups.keys())[groupIndex + 1];
    const currentValues = statusGroups.get(currentKey);
    const nextValues = statusGroups.get(nextKey);
    const final = nextValues ? [...nextValues.slice(1)] : undefined;

    for (let i = 0; currentValues != null && i < currentValues.length; i++) {
        current = current ? current : currentValues[i];

        if (nextValues != null) {
            if (current.nextSteps == null) {
                current.nextSteps = [];
            }
            if (i === 0) {
                const next = createStatusSchema(statusGroups, nextValues[0], groupIndex + 1);
                if (next != null) {
                    current.nextSteps.push(next);
                }
            } else {
                if (final != null) {
                    current.nextSteps.push(...final);
                }
            }
        }
    }

    // When there are no childs
    if (current != null
            && current.nextSteps != null
            && current.nextSteps.length <= 1
            && nextValues != null
            && nextValues.length > 1
            && final != null) {
        current.nextSteps.push(...final);
    }

    return current;
}

export function groupStatus(statusArray: ClaimStatusBaseModel[]): Map<string, ClaimProgressStepModel[]> {
    return statusArray.reduce((map, value) => map.set(value.group, [...map.get(value.group) || [], {
        code: value.code,
        description: value.description,
        longDescription: value.longDescription,
        isCompleted: false
    }]), new Map<string, ClaimProgressStepModel[]>());
}

export function createProgressFromStatusArray(
        statusArray: ClaimStatusBaseModel[],
        schemaArray: ClaimStatusSchemaBaseModel[],
        tooltip: TemplateRef<any>
): ClaimProgressStepModel {
    const statusGroups = groupStatus(statusArray);
    const statusSchema = createStatusSchema(statusGroups);
    return mapProgressFromStatusSchema(schemaArray, tooltip, statusSchema);
}
